import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Document, Page } from "react-pdf";
import { AnimatePresence, motion } from "framer-motion";
import { PAGE_ASPECT, pdfOptions } from "../pdfSetup";
import { LoadingContour } from "./LoadingContour";
import { PDF_URL } from "../lib/api";

const EASE = [0.22, 1, 0.36, 1];

function PageSheet({ pageNumber, height, isMobile, bookmark }) {
  return (
    <div
      className="page-surface book-shadow relative overflow-hidden"
      style={{ height, width: Math.round(height * PAGE_ASPECT) }}
      data-testid={`page-sheet-${pageNumber}`}
    >
      {bookmark && (
        <motion.div
          initial={{ y: -70, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.35 }}
          className="pointer-events-none absolute right-[8%] top-0 z-20 flex flex-col items-center"
          data-testid={`bookmark-ribbon-${pageNumber}`}
          title="Where you left off"
        >
          <div
            className="h-14 w-[18px]"
            style={{
              background: "linear-gradient(180deg, #E3CC98 0%, #C5A059 45%, #A9833F 100%)",
              clipPath: "polygon(0 0, 100% 0, 100% 100%, 50% 78%, 0 100%)",
              boxShadow: "0 6px 12px -5px rgba(60,50,30,0.55), inset 0 1px 0 rgba(255,255,255,0.5)",
            }}
          />
        </motion.div>
      )}
      <Page
        pageNumber={pageNumber}
        height={Math.round(height)}
        renderTextLayer
        renderAnnotationLayer={false}
        loading={
          <div
            className="flex items-center justify-center bg-[#FBFAF7]"
            style={{ height, width: Math.round(height * PAGE_ASPECT) }}
          >
            <span className="h-4 w-4 animate-spin rounded-full border border-gold/30 border-t-gold" />
          </div>
        }
      />
    </div>
  );
}

export const FlipbookReader = ({
  spreads,
  spreadIndex,
  direction,
  isMobile,
  zoom = 1,
  bookmarkPage = null,
  onLoadSuccess,
  pdfUrl = PDF_URL,
}) => {
  const stageRef = useRef(null);
  const measureRef = useRef(null);
  const [dims, setDims] = useState({ w: 0, h: 0 });

  useLayoutEffect(() => {
    const el = measureRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0].contentRect;
      setDims({ w: width, h: height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const [numPages, setNumPages] = useState(null);

  const current = spreads[spreadIndex] || [1];
  // Only pages that actually exist in the loaded PDF render (free edition truncates
  // the book). Size the stage for the VISIBLE page count so a lone last page sits
  // as a single centered page instead of floating in a two-page frame.
  const visiblePages = numPages ? current.filter((p) => p <= numPages) : current;
  const pagesForWidth = isMobile ? 1 : Math.max(1, visiblePages.length);

  // Fit both pages of a spread within the stage while maximizing height, then apply zoom.
  const padded = { w: Math.max(0, dims.w - 8), h: Math.max(0, dims.h - 8) };
  const heightByWidth = padded.w / (PAGE_ASPECT * pagesForWidth);
  const fitHeight = Math.min(padded.h, heightByWidth) || 0;
  const pageHeight = fitHeight * zoom;
  const spreadWidth = pageHeight * PAGE_ASPECT * pagesForWidth;
  const overflowY = pageHeight > padded.h + 1;
  const overflowX = spreadWidth > padded.w + 1;

  const variants = {
    enter: (dir) => ({
      opacity: 0,
      x: dir >= 0 ? 90 : -90,
      rotateY: dir >= 0 ? 9 : -9,
      scale: 0.975,
    }),
    center: { opacity: 1, x: 0, rotateY: 0, scale: 1 },
    exit: (dir) => ({
      opacity: 0,
      x: dir >= 0 ? -90 : 90,
      rotateY: dir >= 0 ? -9 : 9,
      scale: 0.975,
    }),
  };

  const showSpine = !isMobile && visiblePages.length === 2;

  return (
    <div
      ref={stageRef}
      className="reader-nodrag relative h-full w-full overflow-auto"
      onContextMenu={(e) => e.preventDefault()}
      data-testid="flipbook-stage"
    >
      <div ref={measureRef} className="pointer-events-none absolute inset-0" />
      <Document
        file={pdfUrl}
        options={pdfOptions}
        loading={<LoadingContour />}
        error={
          <div className="flex h-full items-center justify-center px-8 text-center text-ink-soft">
            <p className="measure-tight font-display text-xl">
              The book could not be opened right now. Please refresh the page.
            </p>
          </div>
        }
        onLoadSuccess={(pdf) => {
          setNumPages(pdf.numPages);
          onLoadSuccess?.(pdf);
        }}
        externalLinkTarget="_blank"
        className={`flex min-h-full min-w-full p-2 ${overflowX ? "justify-start" : "justify-center"} ${overflowY ? "items-start" : "items-center"}`}
      >
        {pageHeight > 0 && numPages && (
          <AnimatePresence mode="popLayout" custom={direction} initial={false}>
            <motion.div
              key={spreadIndex}
              custom={direction}
              variants={variants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.7, ease: EASE }}
              style={{ transformPerspective: 1800 }}
              className={`relative flex items-stretch justify-center ${showSpine ? "spine" : ""}`}
            >
              {visiblePages.map((p) => (
                <PageSheet key={p} pageNumber={p} height={pageHeight} isMobile={isMobile} bookmark={p === bookmarkPage} />
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </Document>
    </div>
  );
};
